import { PaymentOrder, PaymentProof } from './paymentApi';

export interface RazorpayInstance {
  open(): void;
  close(): void;
  on(event: 'payment.failed', callback: (payload: unknown) => void): void;
}
export interface RazorpayOptions {
  readonly key: string;
  readonly amount: number;
  readonly currency: 'INR';
  readonly order_id: string;
  readonly name: string;
  readonly prefill: Readonly<{ email: string; contact: string; name?: string }>;
  readonly hidden?: Readonly<{ contact: true; email: true }>;
  readonly retry: Readonly<{ enabled: false }>;
  readonly handler: (payload: unknown) => void;
  readonly modal: Readonly<{ ondismiss: () => void }>;
}
export interface RazorpayConstructor {
  new (options: RazorpayOptions): RazorpayInstance;
}
declare global {
  interface Window {
    Razorpay?: RazorpayConstructor;
  }
}
export enum CheckoutOutcomeKind {
  Success = 'success',
  Dismissed = 'dismissed',
  Failed = 'failed',
}
export type CheckoutOutcome =
  | { readonly kind: CheckoutOutcomeKind.Success; readonly proof: PaymentProof }
  | { readonly kind: CheckoutOutcomeKind.Dismissed }
  | { readonly kind: CheckoutOutcomeKind.Failed; readonly message: string };
export interface PaymentCheckout {
  load(): Promise<RazorpayConstructor>;
  open(order: PaymentOrder, key: string): Promise<CheckoutOutcome>;
  close(): void;
}
const scriptUrl = 'https://checkout.razorpay.com/v1/checkout.js';
let loading: Promise<RazorpayConstructor> | undefined;
// App-level loader: loads the official Checkout script once and waits for window.Razorpay.
// https://razorpay.com/docs/payments/payment-gateway/web-integration/standard/integration-steps/
export function loadRazorpay(): Promise<RazorpayConstructor> {
  if (typeof window.Razorpay === 'function') return Promise.resolve(window.Razorpay);
  if (loading) return loading;
  loading = new Promise<RazorpayConstructor>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    const cleanup = () => {
      clearTimeout(timer);
      script.onload = null;
      script.onerror = null;
    };
    const fail = () => {
      cleanup();
      script.remove();
      reject(new Error('Could not load Razorpay. Please try again.'));
    };
    const timer = setTimeout(fail, 15000);
    script.onload = () => {
      if (typeof window.Razorpay !== 'function') {
        fail();
        return;
      }
      cleanup();
      resolve(window.Razorpay);
    };
    script.onerror = fail;
    document.head.appendChild(script);
  }).catch((error: unknown) => {
    loading = undefined;
    throw error;
  });
  return loading;
}
function parseProof(payload: unknown): PaymentProof | null {
  if (
    typeof payload !== 'object' ||
    payload === null ||
    !('razorpay_payment_id' in payload) ||
    typeof payload.razorpay_payment_id !== 'string' ||
    !/^pay_[a-zA-Z0-9]+$/.test(payload.razorpay_payment_id) ||
    !('razorpay_order_id' in payload) ||
    typeof payload.razorpay_order_id !== 'string' ||
    !/^order_[a-zA-Z0-9]+$/.test(payload.razorpay_order_id) ||
    !('razorpay_signature' in payload) ||
    typeof payload.razorpay_signature !== 'string' ||
    !/^[0-9a-f]{64}$/i.test(payload.razorpay_signature)
  )
    return null;
  return {
    razorpay_payment_id: payload.razorpay_payment_id,
    razorpay_order_id: payload.razorpay_order_id,
    razorpay_signature: payload.razorpay_signature,
  };
}
export class BrowserRazorpayCheckout implements PaymentCheckout {
  private instance: RazorpayInstance | null = null;
  constructor(private readonly loader: () => Promise<RazorpayConstructor> = loadRazorpay) {}
  load(): Promise<RazorpayConstructor> {
    return this.loader();
  }
  close(): void {
    this.instance?.close();
    this.instance = null;
  }
  async open(order: PaymentOrder, key: string): Promise<CheckoutOutcome> {
    if (!order.order_id || Date.parse(order.expires_at) <= Date.now())
      throw new Error('Checkout reservation has expired.');
    const Razorpay = await this.load();
    return new Promise((resolve) => {
      let settled = false;
      const finish = (outcome: CheckoutOutcome) => {
        if (!settled) {
          settled = true;
          this.instance = null;
          resolve(outcome);
        }
      };
      const instance = new Razorpay({
        key,
        amount: order.amount,
        currency: order.currency,
        order_id: order.order_id!,
        name: 'Resham Tales',
        // The quote's payer was already confirmed at checkout; don't ask again.
        prefill: { name: order.payer.name, email: order.payer.email, contact: order.payer.phone },
        hidden: { contact: true, email: true },
        retry: { enabled: false },
        handler: (payload: unknown) => {
          const proof = parseProof(payload);
          finish(
            proof && proof.razorpay_order_id === order.order_id
              ? { kind: CheckoutOutcomeKind.Success, proof }
              : {
                  kind: CheckoutOutcomeKind.Failed,
                  message: 'Invalid payment callback. Check payment status.',
                },
          );
        },
        modal: { ondismiss: () => finish({ kind: CheckoutOutcomeKind.Dismissed }) },
      });
      this.instance = instance;
      instance.on('payment.failed', () => {
        finish({
          kind: CheckoutOutcomeKind.Failed,
          message: 'Payment failed. Please check its status before retrying.',
        });
        instance.close();
      });
      instance.open();
    });
  }
}
